import React, { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import type { FrameFieldT } from '../../../../../modernization-common/relationships';
import { TargetsList } from '../../../../../modernization-common/relationships/components/relationship-editor';
import { activeDraftIdAtom, allAnnotationsDataWithFrameRangesAtom, availableTargetsAtom, draftRelationshipsAtom, isEditingAtom, sameIntervalAtom, selectedLabelAtom, sharedFrameRangeAtom, sourceObjectIdAtom, validationErrorsAtom } from '../../../atoms/relationship-atoms';
import { computeTargetFrameRangeLimits, formatCardinalityError } from '../utils';
import { getAnnotationSelectLabel, getHoverableAnnotationSelectLabel } from './annotation-select-label';
type TargetsListWrapperT = {
  isVideoTask: boolean;
};
export const TargetsListWrapper = ({
  isVideoTask
}: TargetsListWrapperT) => {
  const [draftRelationships, setDraftRelationships] = useAtom(draftRelationshipsAtom);
  const isEditing = useAtomValue(isEditingAtom);
  const availableTargets = useAtomValue(availableTargetsAtom);
  const selectedLabel = useAtomValue(selectedLabelAtom);
  const sameInterval = useAtomValue(sameIntervalAtom);
  const sharedFrameRange = useAtomValue(sharedFrameRangeAtom);
  const allAnnotations = useAtomValue(allAnnotationsDataWithFrameRangesAtom);
  const sourceId = useAtomValue(sourceObjectIdAtom);
  const validationErrors = useAtomValue(validationErrorsAtom);
  const [activeDraftId, setActiveDraftId] = useAtom(activeDraftIdAtom);
  const sourceEntry = allAnnotations[sourceId];
  const sourceFrameRange = sourceEntry?.frameRanges[0] ? {
    startFrame: sourceEntry.frameRanges[0].startFrameIndex + 1,
    endFrame: sourceEntry.frameRanges[0].endFrameIndex + 1
  } : {};
  const formattedErrors = useMemo(() => Object.fromEntries(Object.entries(validationErrors).map(([id, e]) => [id, {
    frameRange: e.frameRange,
    error: e.cardinality ? formatCardinalityError(e.cardinality, isVideoTask) : undefined
  }])), [validationErrors, isVideoTask]);
  const handleTargetChange = useCallback((id: string, targetId: string) => {
    setDraftRelationships(prev => {
      const sourceEntry = allAnnotations[sourceId];
      const targetEntry = allAnnotations[targetId];
      if (!sourceEntry || !targetEntry) return prev;
      const draft = prev[id];
      if (!draft) return prev;
      const {
        frameRanges
      } = computeTargetFrameRangeLimits(sourceEntry.frameRanges, targetEntry.frameRanges);

      // Only pre-fill the first intersection window — the user can add more ranges explicitly.
      return {
        ...prev,
        [id]: {
          ...draft,
          targetId,
          frameRanges: frameRanges.slice(0, 1)
        }
      };
    });
  }, [setDraftRelationships, allAnnotations, sourceId]);
  const handleTargetFrameChange = useCallback((id: string, rangeIndex: number, field: FrameFieldT, value: number | undefined) => {
    if (sameInterval) return;
    setDraftRelationships(prev => {
      const draft = prev[id];
      if (!draft) return prev;
      const ranges = [...draft.frameRanges];
      while (ranges.length <= rangeIndex) ranges.push({});
      ranges[rangeIndex] = {
        ...ranges[rangeIndex],
        [field]: value
      };
      return {
        ...prev,
        [id]: {
          ...draft,
          frameRanges: ranges
        }
      };
    });
  }, [sameInterval, setDraftRelationships]);
  const handleAddTarget = useCallback((id: string) => {
    setActiveDraftId(id);
    setDraftRelationships(prev => {
      const draft = prev[id];
      if (!draft) return prev;
      return {
        ...prev,
        [id]: {
          ...draft,
          frameRanges: [sourceFrameRange]
        }
      };
    });
  }, [sourceFrameRange, setDraftRelationships, setActiveDraftId]);
  const handleDraftFocus = useCallback((id: string) => setActiveDraftId(id), [setActiveDraftId]);
  return <TargetsList draftRelationships={draftRelationships} isEditing={isEditing} availableTargets={availableTargets} temporalScope={isVideoTask ? selectedLabel?.temporalConstraints?.temporalScope : undefined} sameInterval={sameInterval} sharedFrameRange={sharedFrameRange} validationErrors={formattedErrors} activeTargetEntryId={activeDraftId} tooltipText="You can also select an object by clicking the bounding box on the canvas." onDraftRelationshipsChange={setDraftRelationships} onAddTarget={handleAddTarget} onTargetEntryFocus={handleDraftFocus} handleTargetChange={handleTargetChange} handleTargetFrameChange={handleTargetFrameChange} noResultsMsg={!selectedLabel ? 'Select a relationship type to see available targets' : undefined} getTargetOptionLabel={getHoverableAnnotationSelectLabel} getTargetValueLabel={getAnnotationSelectLabel} />;
};